import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Game } from './components/Game/Game';
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/Dashboard';
import CaseManager from './components/Admin/CaseManager';
import UserManager from './components/Admin/UserManager';
import CodeManager from './components/Admin/CodeManager';
import StoreManager from './components/Admin/StoreManager';
import { AdminPushPanel } from './components/Admin/PushManager';
import { AnnouncementManager } from './components/Admin/AnnouncementManager';
import { RankManager } from './components/Admin/RankManager';
import Settings from './components/Admin/Settings';
import Login from './components/Login';
import { useAuth, messaging } from './services/firebase';
import { onMessage } from 'firebase/messaging';
import DisclaimerScreen from './components/DisclaimerScreen';
import PwaPrompt from './components/PwaPrompt';

const RequireAuth: React.FC<{ children: React.ReactNode, requiredRole?: 'admin' | 'user' }> = ({ children, requiredRole }) => {
    const { user, role, loading } = useAuth();

    if (loading) return (
        <div className="h-screen w-full bg-zinc-950 flex flex-col items-center justify-center text-white">
            <Search className="w-16 h-16 text-amber-500 animate-bounce mb-4 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]" />
            <div className="text-noir-300 font-mono tracking-widest uppercase animate-pulse text-sm">Investigando Arquivos...</div>
        </div>
    );

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requiredRole && role !== requiredRole && role !== 'admin') {
        return <Navigate to="/game" replace />;
    }

    // New: Block Admins from playing the game (unless they have a specific 'player' override, but sticking to request)
    // If strict separation is desired:
    if (role === 'admin' && !requiredRole) {
        // Assuming !requiredRole means "Public/Game" route in this context, or we can explicit check path
        return <Navigate to="/admin" replace />;
    }

    return children;
};

const App: React.FC = () => {
    useEffect(() => {
        if (!messaging) return;
        const unsubscribe = onMessage(messaging, (payload) => {
            console.log('Push Foreground (Painel Aberto):', payload);
            const title = payload.notification?.title || payload.data?.title || 'Agência ACME';
            const body = payload.notification?.body || payload.data?.body || '';

            if (Notification.permission === 'granted') {
                new Notification(title, { body, icon: '/logo.png' });
            } else {
                alert(`[NOVA MENSAGEM] ${title}\n${body}`);
            }
        });
        return () => unsubscribe();
    }, []);

    return (
        <React.Fragment>
            <DisclaimerScreen />
            <PwaPrompt />
            <BrowserRouter>
                <Routes>
                    {/* Game Route (PWA Default) - Protected */}
                    <Route path="/" element={<Navigate to="/game" replace />} />
                    <Route path="/game" element={
                        <RequireAuth>
                            <Game />
                        </RequireAuth>
                    } />

                    {/* Login Route */}
                    <Route path="/login" element={<Login />} />

                    {/* Admin Routes - Protected */}
                    <Route path="/admin" element={
                        <RequireAuth requiredRole="admin">
                            <AdminLayout />
                        </RequireAuth>
                    }>
                        <Route index element={<AdminDashboard />} />
                        <Route path="cases" element={<CaseManager />} />
                        <Route path="users" element={<UserManager />} />
                        <Route path="ranks" element={<RankManager />} />
                        <Route path="codes" element={<CodeManager />} />
                        <Route path="store" element={<StoreManager />} />
                        <Route path="announcements" element={<AnnouncementManager />} />
                        <Route path="push" element={<AdminPushPanel />} />
                        <Route path="settings" element={<Settings />} />
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/game" replace />} />
                </Routes>
            </BrowserRouter>
        </React.Fragment>
    );
};

export default App;